import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import CurrentPackagesTableCell from 'components/AthletePackages/CurrentPackagesTableCell';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useTrialMembers from 'hooks/reports/athletes/useTrialMembers';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { TrialMemberReport } from 'types/generated';

import { SearchFilters } from '../types';

const TrialMembersTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();
  const { dateFormat } = useLocalization();

  const [ordering, setOrdering] = useState('trialDate');
  const [ascending, setAscending] = useState(false);
  const [tableData, setTableData] = useState<TrialMemberReport[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useTrialMembers({
      ...searchFilters,
      ordering: ascending ? ordering : `-${ordering}`,
      dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
      dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: TrialMemberReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<TrialMemberReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original)}
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      disableSortBy: true,
    },
    {
      Header: 'Trial Start Date',
      accessor: 'trialDate' as const,
      Cell: ({ value }: CellProps<TrialMemberReport>) => {
        return dayjs(value).format(dateFormat);
      },
    },
    {
      Header: 'Current Packages',
      accessor: 'currentPackages' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<TrialMemberReport>) => (
        <CurrentPackagesTableCell currentPackages={row.original.currentPackages ?? []} />
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'trialDate']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default TrialMembersTable;
